import {useMemo} from 'react';
import {isBrandPortal} from '@netx/core/utils';
import {parseActions} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	cartSavedActionAddToCurrentCart,
	cartSavedActionCreatePDF,
	cartSavedActionCreateTMSObjectPackage,
	cartSavedActionDelete,
	cartSavedActionDownload,
	cartSavedActionDuplicate,
	cartSavedActionManageArchive,
	cartSavedActionManageExpiration,
	cartSavedActionReview,
	cartSavedActionRename,
	cartSavedActionShareMenu,
	cartSavedActionToggleFavorite,
} from '../actions';
import {type ISavedCart} from '../schema';
import {useSavedCart} from './useSavedCart';

export type UseSavedCartActionsArguments = {
	id?: number;
	order?: boolean;
	omit?: string[];
	pick?: string[];
};

const portalActions = [
	'saved-cart-add-to-current-cart',
	'saved-cart-create-pdf',
	'saved-cart-create-pdf-download',
	'saved-cart-create-pdf-share-link',
	'saved-cart-share-menu',
	'saved-cart-share-link',
	'saved-cart-share-team-access',
	'saved-cart-rename',
	'saved-cart-delete',
];

export const useSavedCartActions = ({
	id,
	...props
}: UseSavedCartActionsArguments = {}) => {
	const {savedCart, loading} = useSavedCart({id});

	const getActions = useConstCallback(
		({
			savedCart,
			order = true,
			pick,
			omit,
		}: Omit<UseSavedCartActionsArguments, 'id'> & {savedCart?: ISavedCart}) => {
			let actions = !savedCart
				? []
				: [
						cartSavedActionAddToCurrentCart(savedCart, {group: 1}),

						cartSavedActionDownload(savedCart, {group: 2}),
						cartSavedActionCreatePDF(savedCart, {group: 2}),
						cartSavedActionShareMenu(savedCart, {group: 2}),
						cartSavedActionManageArchive(savedCart, {group: 2}),
						cartSavedActionManageExpiration(savedCart, {group: 2}),
						cartSavedActionReview(savedCart, {group: 2}),
						cartSavedActionCreateTMSObjectPackage(savedCart, {group: 2}),
						cartSavedActionRename(savedCart, {group: 2}),
						cartSavedActionDuplicate(savedCart, {group: 2}),
						cartSavedActionToggleFavorite(savedCart, {group: 2}),

						cartSavedActionDelete(savedCart, {
							group: 3,
						}),
				  ];

			if (isBrandPortal()) {
				actions = actions.filter((action) =>
					portalActions.includes(action.name),
				);
			}

			return parseActions({
				actions,
				order,
				omit,
				pick,
				args: [savedCart],
			});
		},
	);

	const actions = useMemo(
		() => getActions({...props, savedCart}),
		[savedCart, props],
	);

	return {
		actions,
		getActions,
		loading,
	};
};

export default useSavedCartActions;
